// First-time user onboarding flow. Walks new users through:
//   1. Welcome — Google / email / sign-in
//   2. Email entry — sends a 6-digit OTP
//   3. OTP verify — code boxes + hidden autofill input
//   4. Name — full name
//   5. Find your church — search & follow (or request) your church
//   6. Follow churches — curated list, multi-select
//   7. Done
// (Profile photo is set later in the in-app profile, not during onboarding.)
// Then sets profile.onboarding_complete = true. The existing 5-step
// walkthrough (Screen 7 in the spec) is the coach-mark sequence already
// inside BibleApp — it triggers naturally on first render.
//
// Existing users already have onboarding_complete = true (via the
// backfill migration) and never see this; they go straight to the app.
import { useEffect, useRef, useState } from 'react'
import { useAuth, DEV_BYPASS_EMAIL } from '../hooks/useAuth'
import { supabase } from '../supabase'
import { OtpCodeInputs } from './Auth'
import Median from 'median-js-bridge'

// True inside the Median native app. The UA token is synchronous on first paint
// (window.median / the JSBridge are injected a moment later).
function inMedianApp() {
  if (typeof navigator !== 'undefined' && /median|gonative/i.test(navigator.userAgent || '')) return true
  if (typeof window !== 'undefined' && window.median) return true
  try { return !!(Median.isNativeApp && Median.isNativeApp()) } catch { return false }
}

// Open a URL in the device's system browser via the Median bridge. Google
// allows OAuth in the system browser (it only blocks embedded WebViews).
function openInSystemBrowser(url) {
  const m = typeof window !== 'undefined' ? window.median : null
  if (m && typeof m.openExternalUrl === 'function') { m.openExternalUrl(url); return }
  try { Median.window.open(url, 'external'); return } catch (_e) { /* fall through */ }
  if (typeof window !== 'undefined') window.location.href = url
}


// Consistent "Skip for now" pinned to the bottom of the screen. Rendered as a
// sibling of the scrollable body inside the flex-column shell, so the flex:1
// body pushes it to the very bottom on every step that allows skipping.
function SkipFooter({ onClick }) {
  return (
    <div style={{flexShrink:0, padding:'10px 24px 18px', textAlign:'center'}}>
      <button type="button" onClick={onClick}
        style={{
          background:'none', border:'none', color:'var(--stone)',
          fontSize:13, cursor:'pointer', padding:8,
          fontFamily:"'Plus Jakarta Sans', system-ui, sans-serif", textDecoration:'underline',
        }}>
        Skip for now
      </button>
    </div>
  )
}

// Welcome-screen rotating headlines (redesign Phase 8a) — replaces the old
// single static "Your Bible, enriched by your church." line.
const WELCOME_HEADLINES = [
  "Sunday's sermon, all week long.",
  'Read the Bible with your church.',
  'Pray for each other.',
]

const ACCENT = 'var(--gold)'
const NAVY   = 'var(--ink)'
const SERIF  = "'Source Serif 4', Georgia, serif"
const SANS   = "'Plus Jakarta Sans', system-ui, sans-serif"
const OTP_LENGTH = 6

const s = {
  shell:   { minHeight:'100vh', background:'var(--cream)', color:'var(--ink)', fontFamily:SANS, display:'flex', flexDirection:'column', paddingTop:'env(safe-area-inset-top)', paddingBottom:'env(safe-area-inset-bottom)' },
  body:    { flex:1, padding:'48px 24px 28px', maxWidth:440, width:'100%', margin:'0 auto', boxSizing:'border-box' },
  back:    { background:'none', border:'none', color:'var(--stone)', fontSize:14, cursor:'pointer', padding:0, marginBottom:24, fontFamily:SANS },
  title:   { fontFamily:SERIF, fontSize:32, lineHeight:1.15, color:'var(--ink)', marginBottom:10 },
  subtitle:{ fontSize:14, color:'var(--stone)', marginBottom:28, lineHeight:1.5 },
  label:   { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--stone)', marginBottom:8, display:'block' },
  input:   { width:'100%', background:'var(--white)', border:'1px solid var(--border-strong)', borderRadius:12, padding:'14px 16px', color:'var(--ink)', fontSize:15, fontFamily:SANS, boxSizing:'border-box', outline:'none' },
  primary: { width:'100%', background:ACCENT, color:NAVY, border:'none', borderRadius:12, padding:'15px', fontSize:15, fontWeight:800, cursor:'pointer', marginTop:18, fontFamily:SANS },
  outline: { width:'100%', background:'transparent', color:'var(--ink)', border:'1px solid var(--border-strong)', borderRadius:12, padding:'15px', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:SANS },
  white:   { width:'100%', background:'#fff', color:'#1f2937', border:'1px solid var(--border)', borderRadius:12, padding:'15px', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:SANS, display:'inline-flex', alignItems:'center', justifyContent:'center', gap:10 },
  dis:     { opacity:0.4, cursor:'default' },
  err:     { background:'rgba(200,55,42,0.1)', border:'1px solid rgba(200,55,42,0.3)', color:'#C8372A', borderRadius:10, padding:'10px 12px', fontSize:13, marginTop:14 },
  skip:    { background:'none', border:'none', color:'var(--stone)', fontSize:13, cursor:'pointer', padding:'12px 16px', fontFamily:SANS, textDecoration:'underline' },
  fine:    { fontSize:11, color:'var(--stone-dim)', textAlign:'center', marginTop:14, lineHeight:1.5 },
  section: { fontSize:11, fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--stone)', marginBottom:10 },
}

function GoogleLogo({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.12-.84 2.07-1.79 2.71v2.26h2.9c1.7-1.57 2.69-3.88 2.69-6.61z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.19l-2.9-2.26c-.81.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.34A8.99 8.99 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.95 10.69A5.4 5.4 0 0 1 3.66 9c0-.59.1-1.16.29-1.69V4.97H.96A8.99 8.99 0 0 0 0 9c0 1.45.35 2.83.96 4.03l2.99-2.34z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A8.99 8.99 0 0 0 .96 4.97l2.99 2.34C4.66 5.18 6.65 3.58 9 3.58z"/>
    </svg>
  )
}

function AppleLogo() {
  return (
    <svg width="15" height="18" viewBox="0 0 384 512" fill="white" aria-hidden="true" style={{display:'block'}}>
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
    </svg>
  )
}

// Apple sign-in is only offered on iOS / inside the Median wrapper.
function appleSignInAvailable() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const isMedian = typeof window.median !== 'undefined'
  return isIOS || isMedian
}

// 4-dot progress indicator for the profile-setup screens (4-6 + walkthrough).
function ProgressDots({ current, total = 4 }) {
  return (
    <div style={{display:'flex', justifyContent:'center', gap:8, marginBottom:32}}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          width: i === current ? 24 : 6, height:6, borderRadius:3,
          background: i === current ? ACCENT : 'rgba(31,27,22,0.15)',
          transition: 'width 200ms, background 200ms',
        }}/>
      ))}
    </div>
  )
}

export default function OnboardingFlow({ startStep = 'welcome', onExplore }) {
  const { user, profile, updateProfile, verifyEmailOtp, authError, clearAuthError, requestAccess } = useAuth()
  // Invite-only churches now show "Request to join" instead of being hidden
  // from search (redesign Phase 8a) — tracks which ones this session has
  // already asked for, so the row can flip to a disabled "Requested" state.
  const [requestedChurchIds, setRequestedChurchIds] = useState(() => new Set())
  async function requestToJoin(churchId) {
    if (requestedChurchIds.has(churchId)) return
    setRequestedChurchIds(prev => new Set(prev).add(churchId))
    await requestAccess('membership', churchId)
  }
  const [step, setStep] = useState(startStep)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [resentNote, setResentNote] = useState('')

  const [fullName, setFullName] = useState('')

  // Welcome-screen rotating headlines + "have an invite code?" escape hatch
  // (redesign Phase 8a).
  const [welcomeHeadlineIdx, setWelcomeHeadlineIdx] = useState(0)
  useEffect(() => {
    if (step !== 'welcome') return
    const t = setInterval(() => setWelcomeHeadlineIdx(i => (i + 1) % WELCOME_HEADLINES.length), 3000)
    return () => clearInterval(t)
  }, [step])
  const [showInviteCodeInput, setShowInviteCodeInput] = useState(false)
  const [inviteCodeValue, setInviteCodeValue] = useState('')
  function applyInviteCode() {
    const raw = inviteCodeValue.trim()
    if (!raw) return
    let token = raw
    try {
      const url = new URL(raw)
      token = url.searchParams.get('invite') || raw
    } catch { /* not a URL — use the raw value as the token */ }
    window.location.href = '/?invite=' + encodeURIComponent(token)
  }

  const [churches, setChurches] = useState([])
  const [churchSearch, setChurchSearch] = useState('')
  // User-led onboarding: follow any church right away (writes user_church_follows,
  // which BibleApp hydrates on mount). No home church required.
  const [followedIds, setFollowedIds] = useState(() => new Set())
  const [followBusy, setFollowBusy]   = useState(null)   // church id mid-toggle
  const [curated, setCurated]         = useState([])     // church_stats rows
  const [curatedLoaded, setCuratedLoaded] = useState(false)
  // Desktop breakpoint — the follow step uses a two-column layout on wide
  // screens (see the follow-churches desktop mockups). Mobile is untouched.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(min-width:768px)').matches
  )
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(min-width:768px)')
    const onCh = e => setIsDesktop(e.matches)
    try { mq.addEventListener('change', onCh) } catch { mq.addListener(onCh) }
    return () => { try { mq.removeEventListener('change', onCh) } catch { mq.removeListener(onCh) } }
  }, [])
  // "Request a church / event / network" form (for entities not yet on the
  // platform). reqKind drives the copy + the entity_type saved on the request.
  const [reqOpen, setReqOpen]         = useState(false)
  const [reqKind, setReqKind]         = useState('church') // 'church' | 'event' | 'network'
  const [reqBusy, setReqBusy]         = useState(false)
  const [reqDone, setReqDone]         = useState(false)
  const [reqName, setReqName]         = useState('')
  const [reqLocation, setReqLocation] = useState('')
  const [reqError, setReqError]       = useState('')
  // Pre-select a church if the user landed here via the landing-page
  // search ("/login?church=<id>"). profile.church_id still wins if it's
  // already set (e.g. they came in via an invite link).
  const [selectedChurchId, setSelectedChurchId] = useState(() => {
    if (profile?.church_id) return profile.church_id
    if (typeof window === 'undefined') return ''
    try {
      const id = new URLSearchParams(window.location.search).get('church')
      if (id) return id
      // Native Google OAuth stashes the pre-sign-in church here because the
      // custom-scheme redirect can't carry a query param (see handleGoogle).
      return localStorage.getItem('nlc.oauthChurch') || ''
    } catch { return '' }
  })
  // Consume the one-shot OAuth church stash so it can't preselect a church on a
  // later, unrelated sign-up (the value is already captured in state above).
  useEffect(() => { try { localStorage.removeItem('nlc.oauthChurch') } catch (_e) { /* ignore */ } }, [])

  // Mirror auth-context errors (e.g. profile setup failing after an OAuth
  // callback) into the on-screen error so they're visible on mobile too.
  useEffect(() => { if (authError) setError(authError) }, [authError])

  // On sign-out, reset to a clean, interactive welcome screen. This component
  // is reused across the auth boundary (to preserve in-flight onboarding data
  // when signing in), so without this a stale `busy` from a prior sign-in
  // would persist and leave the login buttons greyed out / unclickable.
  const prevUserRef = useRef(user)
  useEffect(() => {
    const justLoggedOut = prevUserRef.current && !user
    prevUserRef.current = user
    if (justLoggedOut) {
      setBusy(false)
      setError('')
      setStep(startStep)
    }
  }, [user, startStep])

  // OAuth sets `busy` then relies on a redirect to leave the page. If that
  // doesn't happen (the user cancels the Apple/Google sheet, or the flow
  // returns in-place inside the Median app), re-enable the buttons when the
  // page regains focus so the welcome screen never stays stuck/greyed.
  useEffect(() => {
    const reenable = () => { if (!user && document.visibilityState !== 'hidden') setBusy(false) }
    window.addEventListener('focus', reenable)
    window.addEventListener('pageshow', reenable)
    document.addEventListener('visibilitychange', reenable)
    return () => {
      window.removeEventListener('focus', reenable)
      window.removeEventListener('pageshow', reenable)
      document.removeEventListener('visibilitychange', reenable)
    }
  }, [user])

  // Surface OAuth callback errors. When Apple/Google sign-in fails at the
  // Supabase callback, it redirects back here with the error in the URL query
  // OR hash fragment. Without this we'd just show the welcome screen and the
  // user sees nothing useful (or a stale "sign-up not complete").
  useEffect(() => {
    if (typeof window === 'undefined') return
    const q = new URLSearchParams(window.location.search)
    const h = new URLSearchParams((window.location.hash || '').replace(/^#/, ''))
    const raw = q.get('error_description') || q.get('error') ||
                h.get('error_description') || h.get('error')
    if (!raw) return
    const msg = decodeURIComponent(raw).replace(/\+/g, ' ')
    console.error('OAuth callback error:', msg, { search: window.location.search, hash: window.location.hash })
    setError(msg)
    try {
      const url = new URL(window.location.href)
      ;['error', 'error_description', 'error_code', 'state', 'provider'].forEach(k => url.searchParams.delete(k))
      url.hash = ''
      window.history.replaceState({}, '', url.pathname + (url.search || ''))
    } catch (_e) { /* ignore */ }
  }, [])

  // When the auth context loads a profile mid-flow, seed our local fields.
  // Legacy profiles created via email OTP have full_name set to the email
  // local-part (e.g. "leanne" from leanne@gmail.com); skip seeding in that
  // case so the user enters their real full name.
  useEffect(() => {
    if (profile?.full_name && !fullName) {
      const emailLocal = user?.email?.split('@')[0] || ''
      if (profile.full_name !== emailLocal) {
        setFullName(String(profile.full_name).trim())
      }
    }
    if (profile?.church_id && !selectedChurchId) setSelectedChurchId(profile.church_id)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id])

  // Skip the name screen entirely when a real name is already known (Apple/
  // Google OAuth) — the spec only wants to ask when it's genuinely missing
  // (redesign Phase 8a).
  useEffect(() => {
    if (step !== 'name') return
    const emailLocal = user?.email?.split('@')[0] || ''
    const realName = profile?.full_name && profile.full_name.trim() !== emailLocal
    if (realName) setStep('done')
  }, [step, profile?.full_name, user?.email])

  // Load churches when we land on the church step.
  useEffect(() => {
    if (step !== 'church' || churches.length) return
    supabase.from('churches').select('*').order('name').then(({ data }) => {
      setChurches(Array.isArray(data) ? data : [])
    })
  }, [step, churches.length])

  // Hydrate the user's existing follows so toggles reflect reality on re-entry.
  useEffect(() => {
    if (!user?.id) return
    supabase.from('user_church_follows').select('church_id').eq('user_id', user.id)
      .then(({ data }) => {
        if (Array.isArray(data)) setFollowedIds(new Set(data.map(r => r.church_id).filter(Boolean)))
      })
  }, [user?.id])

  // Curated follow list, ordered by sermon coverage. Prefers the church_stats
  // RPC (accurate cross-church counts); falls back to a plain churches list if
  // the migration hasn't been run yet, so the flow still works.
  useEffect(() => {
    if (step !== 'church' || curatedLoaded) return
    let cancelled = false
    ;(async () => {
      let rows = []
      const { data, error } = await supabase.rpc('church_stats')
      if (!error && Array.isArray(data)) {
        rows = data.map(r => ({
          id: r.church_id, name: r.name, location: r.location, logoUrl: r.logo_url || null,
          networkId: r.network_id || null, networkName: r.network_name || null,
          sermonCount: Number(r.sermon_count) || 0, verseCount: Number(r.verse_count) || 0,
        }))
      } else {
        const { data: cs } = await supabase.from('churches').select('*').order('name')
        rows = (cs || []).filter(c => !c.invite_only).map(c => ({
          id: c.id, name: c.name, location: c.location, logoUrl: c.logo_url || null,
          networkId: c.network_id || null, networkName: null, sermonCount: null, verseCount: null,
        }))
      }
      if (!cancelled) { setCurated(rows); setCuratedLoaded(true); setChurches(prev => prev.length ? prev : rows) }
    })()
    return () => { cancelled = true }
  }, [step, curatedLoaded])

  // If the user signs in mid-flow (e.g. via the verify step) and turns out
  // to already be onboarded, exit by setting the flag (App.js routes them
  // straight into the app on the next render).
  useEffect(() => {
    if (step === 'verify' && user && profile?.onboarding_complete) {
      finish()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profile?.onboarding_complete])

  function authRedirectUrl() {
    if (typeof window === 'undefined') return undefined
    const base = window.location.hostname === 'localhost'
      ? 'http://localhost:3000'
      : 'https://thebiblecommunity.co.uk/login'
    // Carry the landing-page church selection through the OAuth / magic-link
    // redirect. Without this the bare redirect URL drops "?church=<id>", so
    // on return selectedChurchId is empty and we'd ask for the church again.
    return selectedChurchId
      ? `${base}?church=${encodeURIComponent(selectedChurchId)}`
      : base
  }

  async function handleGoogle() {
    setBusy(true); setError('')
    // Google blocks OAuth in embedded WebViews, so inside the app we grab the
    // OAuth URL (skipBrowserRedirect) and open it in the system browser, which
    // Google permits. We return to our OWN https /login page (app_oauth=1), NOT
    // straight to the custom scheme: iOS Safari blocks an automatic 302 to a
    // custom scheme. The https page then shows a tap-to-return button
    // (ReturnToApp) — a user gesture IS allowed to open the scheme — which
    // carries the short PKCE code into the app, where the WebView (holding the
    // code verifier) finishes the exchange.
    const native = inMedianApp()
    let redirectTo = authRedirectUrl()
    if (native) {
      // The custom-scheme hop back into the app can't carry ?church=, so stash
      // it and restore it on return (see the selectedChurchId initialiser).
      try { localStorage.setItem('nlc.oauthChurch', selectedChurchId || '') } catch (_e) { /* ignore */ }
      redirectTo += (redirectTo.includes('?') ? '&' : '?') + 'app_oauth=1'
    }
    const { data, error: oErr } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo, skipBrowserRedirect: native },
    })
    if (oErr) { setError(oErr.message); setBusy(false); return }
    if (native) {
      if (data?.url) openInSystemBrowser(data.url)
      else { setError('Could not start Google sign-in. Please try again.'); setBusy(false) }
    }
    // On the web, skipBrowserRedirect is false so Supabase already redirected.
  }

  const showAppleSignIn = appleSignInAvailable()
  async function handleApple() {
    setBusy(true); setError('')
    const redirectTo = authRedirectUrl()
    const { error: aErr } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo, scopes: 'name email' },
    })
    if (aErr) {
      console.error('Apple sign in error:', aErr)
      setError(`Sign in with Apple failed: ${aErr.message || 'please try again.'}`)
      setBusy(false)
    }
    // On success the browser redirects — no need to clear busy.
  }

  async function sendCode() {
    if (!email.trim()) return
    // Reviewer bypass: don't send a real OTP. The verify step accepts the
    // fixed dev code and mints a session via the dev-signin function.
    if (email.trim().toLowerCase() === DEV_BYPASS_EMAIL) {
      setError(''); setResentNote(''); setStep('verify'); return
    }
    setBusy(true); setError(''); setResentNote('')
    const { error: oErr } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        emailRedirectTo: authRedirectUrl(),
        shouldCreateUser: true,
      },
    })
    setBusy(false)
    if (oErr) {
      setError(oErr.message || 'Could not send the sign-in code.')
      return
    }
    setStep('verify')
  }

  async function handleVerify(submittedCode) {
    const token = String(submittedCode ?? code).trim()
    if (token.length < OTP_LENGTH) return
    setBusy(true); setError('')
    // Route through useAuth so the reviewer bypass (DEV_BYPASS_EMAIL + code)
    // is honored here too; for everyone else this is a plain email-OTP verify.
    const { error: vErr } = await verifyEmailOtp(email.trim(), token)
    setBusy(false)
    if (vErr) {
      setError('Invalid code — please try again')
      setCode('')
      return
    }
    // Profile may take a moment to surface via the auth listener; default
    // to the "find your church" screen and let the auto-skip effect bail out
    // if the profile turns out to be complete. (Name is collected near the end.)
    setStep('church')
  }

  async function handleResend() {
    if (!email.trim()) return
    // Reviewer bypass uses a fixed code — nothing to resend.
    if (email.trim().toLowerCase() === DEV_BYPASS_EMAIL) {
      setResentNote('New code sent.'); return
    }
    setBusy(true); setResentNote(''); setError('')
    const { error: rErr } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: authRedirectUrl(), shouldCreateUser: true },
    })
    setBusy(false)
    if (rErr) setError(rErr.message || 'Could not resend the code.')
    else setResentNote('New code sent.')
  }

  async function saveName() {
    const trimmed = fullName.trim()
    if (!trimmed) { setStep('done'); return }
    setBusy(true); setError('')
    const { error: pErr } = await updateProfile({ full_name: trimmed })
    setBusy(false)
    if (pErr) { setError(pErr.message); return }
    setStep('done')
  }


  async function finish() {
    // Set the flag (and the home church, if one was chosen in Step 1). Done at
    // the very end so writing church_id can't flip needsOnboarding and eject
    // the user mid-flow. Tolerate the column not existing yet by ignoring the
    // error — App.js treats "onboarding_complete !== false" as completed.
    try {
      const updates = { onboarding_complete: true }
      if (selectedChurchId) updates.church_id = selectedChurchId
      await updateProfile(updates)
    } catch (_e) { /* column may not exist yet */ }
    try { localStorage.setItem('nlc.onboardingDone', '1') } catch (_e) {}
  }

  // "Explore your Bible" from the done step. If they followed anything, land on
  // a chapter rich with sermon dots (Romans 8) so the value is immediate.
  // Navigation goes through the parent's router (onExplore → navigateTo) so the
  // path state updates — a raw history.replaceState leaves App.js on the old
  // route and renders a blank screen.
  async function exploreFinish() {
    await finish()
    const target = followedIds.size > 0
      ? '/app?screen=bible&book=Romans&chapter=8&open=sermonhint'
      : '/app'
    if (onExplore) { onExplore(target); return }
    // Fallback (no callback): change the URL and fire popstate so the router reacts.
    try {
      window.history.replaceState({}, '', target)
      window.dispatchEvent(new PopStateEvent('popstate'))
    } catch (_e) { /* ignore */ }
  }

  // Follow / unfollow a church. Writes user_church_follows directly (the same
  // table BibleApp hydrates from), so follows made here show up in the app
  // immediately — no home church required. Optimistic, with revert on failure.
  async function toggleFollow(churchId) {
    if (!user?.id || !churchId || followBusy) return
    const isFollowing = followedIds.has(churchId)
    setFollowBusy(churchId)
    setFollowedIds(prev => {
      const n = new Set(prev)
      if (isFollowing) n.delete(churchId); else n.add(churchId)
      return n
    })
    try {
      if (isFollowing) {
        const { error } = await supabase.from('user_church_follows')
          .delete().eq('user_id', user.id).eq('church_id', churchId)
        if (error) throw error
      } else {
        const { error } = await supabase.from('user_church_follows')
          .upsert({ user_id: user.id, church_id: churchId }, { onConflict: 'user_id,church_id' })
        if (error) throw error
      }
    } catch (e) {
      // Revert the optimistic toggle.
      setFollowedIds(prev => {
        const n = new Set(prev)
        if (isFollowing) n.add(churchId); else n.delete(churchId)
        return n
      })
      // eslint-disable-next-line no-console
      console.warn('toggleFollow failed:', e?.message || e)
    } finally {
      setFollowBusy(null)
    }
  }

  // Submit a "please add my church" request: store the row (RLS scopes it to
  // the user) and email the team + an auto-reply via the edge function.
  // Open the request sheet pre-set to a kind ('church' | 'event' | 'network').
  function openRequest(kind = 'church') {
    setReqKind(kind); setReqDone(false); setReqError(''); setReqOpen(true)
  }

  async function submitChurchRequest() {
    const label = reqKind === 'event' ? 'Event' : reqKind === 'network' ? 'Network' : 'Church'
    if (!reqName.trim() || !reqLocation.trim()) {
      setReqError(`${label} name and location are required.`); return
    }
    setReqBusy(true); setReqError('')
    const requesterName  = (fullName || profile?.full_name || user?.email?.split('@')[0] || '').trim()
    const requesterEmail = (user?.email || '').trim()
    try {
      // Best-effort row insert (tolerate the table / entity_type column not
      // existing yet — retry without entity_type on that specific error).
      const baseRow = {
        requested_by:        user?.id || null,
        church_name:         reqName.trim(),
        location:            reqLocation.trim(),
        requester_name:      requesterName || null,
        requester_email:     requesterEmail || null,
      }
      let { error: insErr } = await supabase.from('church_requests').insert({ ...baseRow, entity_type: reqKind })
      if (insErr && /entity_type/i.test(insErr.message || '')) {
        ;({ error: insErr } = await supabase.from('church_requests').insert(baseRow))
      }
      if (insErr) console.warn('church_requests insert failed:', insErr.message)
      // Email notification + auto-reply (best-effort).
      await supabase.functions.invoke('submit-church-enquiry', {
        body: {
          type:                'church_request',
          entity_type:         reqKind,
          name:                requesterName || 'A user',
          email:               requesterEmail,
          church:              reqName.trim(),
          location:            reqLocation.trim(),
        },
      })
      setReqDone(true)
    } catch (e) {
      setReqError(e?.message || String(e))
    } finally {
      setReqBusy(false)
    }
  }

  // ───────────────────────────── Renders ───────────────────────────────

  if (step === 'welcome') {
    return (
      <div style={s.shell}>
        <style>{'@keyframes nlc-fade-in{from{opacity:0}to{opacity:1}}'}</style>
        <div style={{...s.body, paddingTop:80, display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center'}}>
          <div style={{fontSize:13, fontWeight:700, color:'var(--gold-ink)', letterSpacing:'0.15em', textTransform:'uppercase', fontFamily:"'JetBrains Mono', monospace", marginBottom:36, marginTop:8}}>The Bible Community</div>
          <h1 key={welcomeHeadlineIdx} style={{...s.title, fontSize:32, textAlign:'center', marginBottom:40, lineHeight:1.2, minHeight:80, animation:'nlc-fade-in 0.6s ease'}}>
            {WELCOME_HEADLINES[welcomeHeadlineIdx]}
          </h1>
          <div style={{width:'100%', display:'flex', flexDirection:'column', gap:10}}>
            {showAppleSignIn && (
              <button type="button" onClick={handleApple} disabled={busy}
                style={{
                  width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:10,
                  background:'#000000', color:'#ffffff', border:'1px solid rgba(255,255,255,0.25)',
                  borderRadius:8, padding:'14px 24px', fontSize:15, fontWeight:600,
                  cursor:'pointer', fontFamily:SANS, ...(busy ? s.dis : {}),
                }}>
                <AppleLogo/> Continue with Apple
              </button>
            )}
            <button type="button" onClick={handleGoogle} disabled={busy} style={{...s.white, ...(busy ? s.dis : {})}}>
              <GoogleLogo size={18}/> Continue with Google
            </button>
            <button type="button" onClick={()=>{ setError(''); setStep('email') }} disabled={busy} style={{...s.primary, marginTop:0, ...(busy ? s.dis : {})}}>
              Continue with email
            </button>
          </div>
          {error && <div style={s.err}>{error}</div>}

          {showInviteCodeInput ? (
            <form onSubmit={e => { e.preventDefault(); applyInviteCode() }}
              style={{width:'100%', marginTop:20, display:'flex', gap:8}}>
              <input autoFocus value={inviteCodeValue} onChange={e=>setInviteCodeValue(e.target.value)}
                placeholder="Invite link or code" style={{...s.input, marginBottom:0}}/>
              <button type="submit" disabled={!inviteCodeValue.trim()} style={{...s.primary, marginTop:0, width:'auto', padding:'0 18px'}}>Go</button>
            </form>
          ) : (
            <button type="button" onClick={()=>setShowInviteCodeInput(true)}
              style={{
                marginTop:20, background:'none', border:'none', color:'var(--gold-ink)',
                fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:SANS, textDecoration:'underline',
              }}>
              Have an invite link or code?
            </button>
          )}

          <a href="/privacy"
            style={{
              marginTop:28, fontSize:11, color:'var(--stone)',
              textDecoration:'underline', letterSpacing:'0.08em',
              textTransform:'uppercase', fontFamily:"'JetBrains Mono', monospace",
            }}>
            Privacy policy
          </a>
        </div>
      </div>
    )
  }

  if (step === 'email') {
    return (
      <div style={s.shell}>
        <div style={s.body}>
          <button onClick={()=>setStep('welcome')} style={s.back}>← Back</button>
          <h1 style={s.title}>What's your email?</h1>
          <p style={s.subtitle}>We'll send you a sign-in code. No password needed.</p>
          <form onSubmit={e => { e.preventDefault(); sendCode() }}>
            <label style={s.label}>Email</label>
            <input
              style={s.input}
              type="email" autoComplete="email"
              autoFocus
              value={email}
              onChange={e=>setEmail(e.target.value)}
              placeholder="you@example.com"/>
            <button type="submit" disabled={!email.trim() || busy}
              style={{...s.primary, ...((!email.trim() || busy) ? s.dis : {})}}>
              {busy ? 'Sending…' : 'Send code →'}
            </button>
          </form>
          <div style={s.fine}>We'll never share your email with anyone.</div>
          {error && <div style={s.err}>{error}</div>}
        </div>
      </div>
    )
  }

  if (step === 'verify') {
    return (
      <div style={s.shell}>
        <div style={s.body}>
          <button onClick={()=>setStep('email')} style={s.back}>← Back</button>
          <div style={{display:'flex', justifyContent:'center', marginBottom:24}}>
            <div style={{
              width:56, height:56, borderRadius:'50%',
              background:'rgba(232,200,122,0.15)', border:`1px solid rgba(232,200,122,0.4)`,
              display:'flex', alignItems:'center', justifyContent:'center', color:'var(--gold-ink)', fontSize:24,
            }}>✉</div>
          </div>
          <h1 style={{...s.title, textAlign:'center'}}>Check your email</h1>
          <p style={{...s.subtitle, textAlign:'center', marginBottom:8}}>
            Enter the {OTP_LENGTH} digit code sent to
          </p>
          <div style={{textAlign:'center', color:'var(--gold-ink)', fontSize:14, fontWeight:700, marginBottom:24}}>
            {email}
          </div>
          <form autoComplete="one-time-code"
            onSubmit={e => { e.preventDefault(); if (!busy) handleVerify(code) }}>
            <OtpCodeInputs
              length={OTP_LENGTH}
              value={code}
              onChange={setCode}
              onSubmit={(full) => { if (!busy) handleVerify(full) }}
              disabled={busy}
              autoFocus/>
            {busy && (
              <div style={{textAlign:'center', fontSize:12, color:'var(--stone)', marginTop:14}}>
                Verifying…
              </div>
            )}
            {error && <div style={s.err}>{error}</div>}
            {resentNote && (
              <div style={{textAlign:'center', fontSize:12, color:'#1F7A52', marginTop:14}}>{resentNote}</div>
            )}
          </form>
          <div style={{textAlign:'center', marginTop:24}}>
            <button type="button" onClick={handleResend} disabled={busy}
              style={{...s.skip, color:'var(--gold-ink)', textDecoration:'underline'}}>
              Didn't get it? Resend code
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (step === 'name') {
    return (
      <div style={s.shell}>
        <div style={s.body}>
          <button onClick={()=>setStep('church')} style={s.back}>← Back</button>
          <ProgressDots current={2} total={3}/>
          <h1 style={s.title}>What's your name?</h1>
          <p style={s.subtitle}>So your church community knows it's you.</p>
          <form onSubmit={e => { e.preventDefault(); saveName() }}>
            <label style={s.label}>Full name</label>
            <input
              style={s.input}
              autoFocus autoComplete="name"
              value={fullName}
              onChange={e=>setFullName(e.target.value)}
              placeholder="e.g. Sarah Mitchell"/>
            <button type="submit" disabled={busy}
              style={{...s.primary, ...(busy ? s.dis : {})}}>
              {busy ? 'Saving…' : 'Continue →'}
            </button>
          </form>
          {error && <div style={s.err}>{error}</div>}
        </div>
        <SkipFooter onClick={()=>setStep('done')}/>
      </div>
    )
  }

  if (step === 'church') {
    const q = churchSearch.trim().toLowerCase()
    // Invite-only churches now appear in results too, with "Request to join"
    // instead of joining straight away (redesign Phase 8a).
    const filtered = !q ? [] : churches.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.location || '').toLowerCase().includes(q)
    )

    // ── DESKTOP: two-column find-your-church (see mockup) ──
    if (isDesktop) {
      const deskList = churches.filter(c =>
        !q || (c.name || '').toLowerCase().includes(q) || (c.location || '').toLowerCase().includes(q))
      // Click a church to set it as home + follow, then go straight to Done
      // — no separate "follow more churches" step any more (that now lives
      // in the Sermons tab's "Find churches" view).
      const pick = (c) => { setSelectedChurchId(c.id); if (!followedIds.has(c.id)) toggleFollow(c.id); setChurchSearch(''); setStep('name') }
      const arrowIco = <span className="nlc-go"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></svg></span>
      return (
        <div className="nlc-fb">
          <div className="nlc-follow-shell">
            <aside className="nlc-rail">
              <div className="nlc-steps"><span className="seg now"/><span className="seg"/><span className="seg"/></div>
              <h1>Find your church</h1>
              <p className="nlc-lede">Search to see if your church is already on The Bible Community.</p>
              <div className="nlc-why">
                <span className="dot"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l9-7 9 7"/><path d="M5 10v10h14V10"/></svg></span>
                <p>This becomes your <b>home church</b> — its sermon notes lead on every verse. You can follow more any time from the Sermons tab.</p>
              </div>
              <div className="nlc-cta">
                <p className="nlc-help">Can&apos;t find your church?{' '}
                  <button onClick={()=>{ openRequest('church'); setReqName(churchSearch.trim()) }}>Request we add it</button>
                  {' · '}
                  <button onClick={()=>{ setChurchSearch(''); setStep('name') }}>Skip for now</button>
                </p>
              </div>
            </aside>

            <main className="nlc-content">
              <div className="nlc-eyebrow">Search churches</div>
              <div className="nlc-fsearch">
                <span className="icn"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg></span>
                <input autoFocus value={churchSearch} onChange={e=>setChurchSearch(e.target.value)} placeholder="Start typing a church name…"/>
              </div>
              {deskList.length === 0 ? (
                <div className="nlc-empty">No churches match &quot;{churchSearch.trim()}&quot;.{' '}
                  <button onClick={()=>{ openRequest('church'); setReqName(churchSearch.trim()) }}>Request we add it</button> and we&apos;ll let you know when it&apos;s live.</div>
              ) : (
                <div className="nlc-list">
                  {deskList.map(c => {
                    const requested = requestedChurchIds.has(c.id)
                    return (
                      <div key={c.id} className={'nlc-crow pick'+(c.id===selectedChurchId?' sel':'')}
                        onClick={c.invite_only ? ()=>requestToJoin(c.id) : ()=>pick(c)}>
                        <div className="nlc-avatar">{(c.name||'?').trim()[0]?.toUpperCase() || '?'}</div>
                        <div className="nlc-ch-meta">
                          <div className="nlc-cname">{c.name}</div>
                          {c.location && <div className="nlc-sub">{c.location}</div>}
                        </div>
                        {c.invite_only ? (
                          <span style={{flexShrink:0, fontSize:12.5, fontWeight:700, color: requested ? 'var(--stone-dim)' : 'var(--gold-ink)'}}>
                            {requested ? 'Requested ✓' : 'Request to join'}
                          </span>
                        ) : arrowIco}
                      </div>
                    )
                  })}
                </div>
              )}
            </main>
          </div>
          {renderRequestSheet()}
        </div>
      )
    }

    return (
      <div className="nlc-card-md" style={s.shell}>
        <div className="nlc-body-md" style={s.body}>
          <ProgressDots current={0} total={3}/>
          <h1 style={s.title}>Find your church</h1>
          <p style={s.subtitle}>Search to see if your church is already on The Bible Community.</p>
          <label style={s.label}>Search churches</label>
          <input
            style={s.input}
            autoFocus
            value={churchSearch}
            onChange={e=>setChurchSearch(e.target.value)}
            placeholder="Start typing a church name…"/>

          {q && (
            <div style={{
              maxHeight:320, overflowY:'auto', marginTop:10,
              border:'1px solid var(--border-strong)', borderRadius:12,
            }}>
              {filtered.length === 0 ? (
                <div style={{padding:'14px 16px', fontSize:13, color:'var(--stone)'}}>
                  No churches match "{churchSearch}".
                </div>
              ) : filtered.map(c => {
                const initial = (c.name || '?').trim()[0]?.toUpperCase() || '?'
                const requested = requestedChurchIds.has(c.id)
                return (
                  <div key={c.id}
                    onClick={c.invite_only
                      ? ()=>requestToJoin(c.id)
                      : async ()=>{ setSelectedChurchId(c.id); if (!followedIds.has(c.id)) await toggleFollow(c.id); setChurchSearch(''); setStep('name') }}
                    style={{
                      display:'flex', alignItems:'center', gap:12,
                      padding:'12px 14px', cursor:'pointer',
                      borderBottom:'1px solid var(--border)',
                      background:'transparent',
                    }}>
                    <div style={{
                      flexShrink:0, width:34, height:34, borderRadius:9,
                      background:'rgba(232,200,122,0.12)',
                      border:'1px solid rgba(232,200,122,0.25)',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:14, fontWeight:700, color:'var(--gold-ink)',
                    }}>{initial}</div>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontSize:14, fontWeight:600, color:'var(--ink)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{c.name}</div>
                      {c.location && (
                        <div style={{fontSize:12, color:'var(--stone)', marginTop:1}}>{c.location}</div>
                      )}
                    </div>
                    {c.invite_only ? (
                      <span style={{flexShrink:0, fontSize:12.5, fontWeight:700, color: requested ? 'var(--stone-dim)' : 'var(--gold-ink)'}}>
                        {requested ? 'Requested ✓' : 'Request to join'}
                      </span>
                    ) : (
                      <span style={{flexShrink:0, fontSize:18, color:'var(--stone)'}} aria-hidden="true">→</span>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {error && <div style={s.err}>{error}</div>}

          {/* Can't find your church → request it, or skip for now. */}
          <div style={{marginTop:16, fontSize:12, color:'var(--stone)', lineHeight:1.6}}>
            Can't find your church?{' '}
            <span onClick={()=>{ openRequest('church'); setReqName(churchSearch.trim()) }}
              style={{color:'var(--gold-ink)', fontWeight:700, textDecoration:'underline', cursor:'pointer'}}>
              Request we add it
            </span>{' '}or{' '}
            <span onClick={()=>{ setChurchSearch(''); setStep('name') }}
              style={{color:'var(--gold-ink)', fontWeight:700, textDecoration:'underline', cursor:'pointer'}}>
              skip for now
            </span>
          </div>
        </div>
        {renderRequestSheet()}
      </div>
    )
  }

  // ── STEP 3 — Done ───────────────────────────────────────────────────────
  if (step === 'done') {
    const followedCurated = curated.filter(c => followedIds.has(c.id))
    const totalSermons = followedCurated.reduce((a, c) => a + (c.sermonCount || 0), 0)
    const n = followedIds.size
    return (
      <div style={s.shell}>
        <div style={{...s.body, display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', justifyContent:'center'}}>
          <div style={{
            width:72, height:72, borderRadius:'50%', background:'rgba(232,200,122,0.12)',
            border:`1px solid rgba(232,200,122,0.3)`, display:'flex', alignItems:'center', justifyContent:'center',
            marginBottom:24,
          }}>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="var(--gold-ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 6 9 17l-5-5"/>
            </svg>
          </div>
          <h1 style={{...s.title, textAlign:'center'}}>{n > 0 ? "You're all set!" : 'Your Bible is ready'}</h1>
          <p style={{...s.subtitle, textAlign:'center'}}>
            {n > 0
              ? `You're following ${n} ${n === 1 ? 'church' : 'churches'}${totalSermons > 0 ? ` with ${totalSermons.toLocaleString()} sermons` : ''}.`
              : 'Start reading and sermon dots will appear as you follow churches.'}
          </p>
          <button type="button" onClick={exploreFinish} style={{...s.primary, marginTop:8}}>Explore your Bible →</button>
        </div>
        {renderRequestSheet()}
      </div>
    )
  }

  // Anything else — fall through; the parent re-renders and routes into
  // BibleApp because onboarding_complete is now true.
  return null

  function renderRequestSheet() {
    if (!reqOpen) return null
    return (
      <>
        <div onClick={()=>setReqOpen(false)} style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:300}}/>
        <div style={{
          position:'fixed', left:'50%', bottom:0, transform:'translateX(-50%)', zIndex:301,
          width:'100%', maxWidth:440, background:'var(--white)', borderRadius:'24px 24px 0 0',
          borderTop:'1px solid var(--border-strong)', padding:'24px 24px max(24px, env(safe-area-inset-bottom))',
          maxHeight:'92vh', overflowY:'auto', boxSizing:'border-box', fontFamily:SANS,
        }}>
          <div style={{width:38, height:4, borderRadius:2, background:'rgba(31,27,22,0.18)', margin:'0 auto 18px'}}/>
          {reqDone ? (
            <div style={{textAlign:'center', padding:'10px 0 6px'}}>
              <div style={{
                width:64, height:64, borderRadius:'50%', background:'rgba(232,200,122,0.12)',
                border:`1px solid rgba(232,200,122,0.3)`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 18px',
              }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="var(--gold-ink)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
              </div>
              <div style={{...s.title, fontSize:26}}>Request sent!</div>
              <p style={{...s.subtitle, marginBottom:22}}>
                We'll add {reqName.trim() || (reqKind === 'event' ? 'your event' : reqKind === 'network' ? 'your network' : 'your church')} and let you know when it's ready — usually within 24 hours.
              </p>
              <button type="button" onClick={()=>{ setReqOpen(false); setStep('name') }} style={s.primary}>
                Keep exploring while you wait →
              </button>
            </div>
          ) : (() => {
            const kindLabel = reqKind === 'event' ? 'event' : reqKind === 'network' ? 'network' : 'church'
            const kinds = [['church','Church'],['event','Event'],['network','Network']]
            return (
            <>
              <h2 style={{...s.title, fontSize:26}}>Request we add {reqKind === 'event' ? 'an' : 'a'} {kindLabel}</h2>
              <p style={{...s.subtitle, marginBottom:18}}>We'll add all of this {kindLabel}'s recorded sermons and content — usually within 24 hours.</p>

              <label style={s.label}>Type</label>
              <div style={{display:'flex', gap:8, marginBottom:14}}>
                {kinds.map(([id, lbl]) => {
                  const on = reqKind === id
                  return (
                    <button key={id} type="button" onClick={()=>setReqKind(id)}
                      style={{
                        flex:1, cursor:'pointer', fontFamily:SANS, fontSize:13, fontWeight:700,
                        borderRadius:10, padding:'9px 8px',
                        background:on?ACCENT:'transparent', color:on?NAVY:'var(--stone)',
                        border:`1px solid ${on?ACCENT:'var(--border-strong)'}`,
                      }}>{lbl}</button>
                  )
                })}
              </div>

              <label style={s.label}>{reqKind === 'event' ? 'Event' : reqKind === 'network' ? 'Network' : 'Church'} name *</label>
              <input style={{...s.input, marginBottom:14}} value={reqName} onChange={e=>setReqName(e.target.value)}
                placeholder={reqKind === 'event' ? 'e.g. New Wine' : reqKind === 'network' ? 'e.g. New Frontiers' : 'e.g. Hope Church'}/>
              <label style={s.label}>Location *</label>
              <input style={{...s.input, marginBottom:18}} value={reqLocation} onChange={e=>setReqLocation(e.target.value)}
                placeholder={reqKind === 'event' ? 'Where it takes place (or "Online")' : 'Town / city'}/>

              {reqError && <div style={s.err}>{reqError}</div>}
              <button type="button" onClick={submitChurchRequest} disabled={reqBusy}
                style={{...s.primary, ...(reqBusy ? s.dis : {}), marginTop:8}}>
                {reqBusy ? 'Sending…' : 'Send request →'}
              </button>
              <button type="button" onClick={()=>setReqOpen(false)} style={{...s.skip, display:'block', margin:'10px auto 0'}}>Cancel</button>
            </>
            )
          })()}
        </div>
      </>
    )
  }
}
